import { useEffect, useRef, useState } from 'preact/hooks';
import type { NormalizedTranscript, Transcript } from '../../shared/types';
import { api } from '../api';
import { sanitizeShowNotesHtml } from '../lib/sanitize';
import { formatTime, positionSec, seekTo } from '../state/player';

/**
 * Transcript viewer with follow-along highlighting.
 *
 * The Worker normalizes VTT / SRT / Podcast Index JSON / HTML into one cue shape, so this
 * component only ever deals with `{startTime, endTime, speaker, body}`.
 */
export function TranscriptPanel({ episodeId }: { episodeId: number }) {
  const [transcript, setTranscript] = useState<NormalizedTranscript | null>(null);
  const [available, setAvailable] = useState<Transcript[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [follow, setFollow] = useState(true);
  const activeRef = useRef<HTMLLIElement>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    api
      .transcript(episodeId)
      .then((res) => {
        if (cancelled) return;
        setTranscript(res.transcript);
        setAvailable(res.available);
      })
      .catch((err: Error) => {
        if (!cancelled) setError(err.message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [episodeId]);

  const cues = transcript?.cues ?? [];
  const activeIndex = findActiveCue(cues, positionSec.value);

  // Keep the current line in view, but stop fighting the user once they scroll away.
  useEffect(() => {
    if (!follow || !activeRef.current) return;
    activeRef.current.scrollIntoView({ block: 'center', behavior: 'smooth' });
  }, [activeIndex, follow]);

  if (loading) return <p class="muted">Loading transcript…</p>;
  if (error) return <p class="muted">Transcript unavailable: {error}</p>;
  if (!transcript) return <p class="muted">This episode has no transcript.</p>;

  if (cues.length === 0 && transcript.html) {
    return (
      <div
        class="prose"
        // Publisher-supplied transcript HTML, fetched verbatim from a feed-hosted URL by the
        // Worker (see src/worker/lib/transcript.ts) — sanitized to a safe subset before
        // rendering, same as show notes. See src/client/lib/sanitize.ts.
        dangerouslySetInnerHTML={{ __html: sanitizeShowNotesHtml(transcript.html) }}
      />
    );
  }

  if (cues.length === 0) return <p class="muted">This transcript appears to be empty.</p>;

  return (
    <div class="transcript">
      <div class="transcript__toolbar">
        <label class="transcript__follow">
          <input
            type="checkbox"
            checked={follow}
            onChange={(e) => setFollow((e.target as HTMLInputElement).checked)}
          />
          Follow along
        </label>
        {available.length > 1 && (
          <select
            class="transcript__picker"
            aria-label="Transcript version"
            onChange={(e) => {
              const url = (e.target as HTMLSelectElement).value;
              setLoading(true);
              api
                .transcript(episodeId, url)
                .then((res) => setTranscript(res.transcript))
                .catch((err: Error) => setError(err.message))
                .finally(() => setLoading(false));
            }}
          >
            {available.map((t) => (
              <option key={t.url} value={t.url}>
                {t.language ?? 'Default'}
                {t.rel === 'captions' ? ' (captions)' : ''}
              </option>
            ))}
          </select>
        )}
      </div>

      <ol class="transcript__cues">
        {cues.map((cue, i) => (
          <li
            key={`${cue.startTime}-${i}`}
            ref={i === activeIndex ? activeRef : undefined}
            class={`transcript__cue ${i === activeIndex ? 'is-active' : ''}`}
          >
            <button type="button" onClick={() => seekTo(cue.startTime)}>
              <span class="transcript__time">{formatTime(cue.startTime)}</span>
              <span class="transcript__body">
                {cue.speaker && <strong class="transcript__speaker">{cue.speaker}: </strong>}
                {cue.body}
              </span>
            </button>
          </li>
        ))}
      </ol>
    </div>
  );
}

/** Binary search for the cue containing `time`; transcripts run to thousands of cues. */
function findActiveCue(cues: NormalizedTranscript['cues'], time: number): number {
  if (cues.length === 0) return -1;
  let lo = 0;
  let hi = cues.length - 1;
  let best = -1;
  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    if (cues[mid].startTime <= time) {
      best = mid;
      lo = mid + 1;
    } else {
      hi = mid - 1;
    }
  }
  // Past the end of a cue with a known end time and no successor started yet.
  if (best >= 0 && cues[best].endTime !== undefined && time > (cues[best].endTime as number)) {
    const next = cues[best + 1];
    if (!next || next.startTime > time) return best;
  }
  return best;
}
